import {
  BottomSheetBackdrop,
  BottomSheetFooter,
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
  type BottomSheetBackdropProps,
  type BottomSheetFooterProps,
  type BottomSheetModalProps,
} from "@-/com.npmjs.gorhom__bottom-sheet";
import {
  PortalContextBridgeContent,
  usePortalContextValues,
} from "@-/dev.improve.haniverse.components.ui.portal-context-bridge";
import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouteBodyRetainer } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { StyleSheet, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useBottomSheetHostSafeAreaInsets } from "@-/dev.improve.haniverse.components.ui.bottom-sheet-provider";
import { useKeyboardState } from "@-/com.npmjs.react-native-keyboard-controller";
import { BottomSheetContentErrorBoundary } from "#bottom-sheet-content-error-boundary";

// Re-export the keyboard-aware text input so consumers don't reach into the raw
// `@gorhom/bottom-sheet` package for inputs hosted inside a `BottomSheet`.
export { BottomSheetTextInput } from "@-/com.npmjs.gorhom__bottom-sheet";

// Sheets use the platform-style 28pt curve, rather than ordinary surfaces' rounded-xs.
// Gorhom's backgroundStyle only accepts native geometry.
const SHEET_RADIUS = 28;
const HANDLE_WIDTH = 36;
const HANDLE_HEIGHT = 4;
const HANDLE_CONTAINER_HEIGHT = 24;
// Minimum visible gap kept below the top safe-area inset so tall sheets never
// cover the whole screen — the backdrop peeking through reads as "bottom sheet".
const SHEET_TOP_GAP = 24;
const FOOTER_CONTENT_INSET = 56;
const LIGHT_BACKGROUND = "#FFFFFF";
const DARK_BACKGROUND = "#0A0A0A";

const styles = StyleSheet.create({
  handleIndicator: {
    backgroundColor: "rgba(120,120,120,0.5)",
    borderRadius: HANDLE_HEIGHT / 2, // rounded-full for the native-only handle style API.
    height: HANDLE_HEIGHT,
    width: HANDLE_WIDTH,
  },
});

export interface BottomSheetHandle {
  close: () => void;
  open: () => void;
}

type BottomSheetProps = {
  /**
   * Receives the sheet's position as it moves: -1 closed, 0 open, and in between while it slides
   * or is dragged. Owned by the caller, so it outlives the sheet's mount.
   */
  animatedIndex?: BottomSheetModalProps["animatedIndex"];
  children: ReactNode;
  /** Disable when sheet content owns pan gestures, such as a zoomable canvas. */
  enableContentPanningGesture?: boolean;
  /** Optional floating footer pinned above the sheet's bottom safe area. */
  footer?: ReactNode;
  /** Open at the maximum height below the top safe area. */
  fullHeight?: boolean;
  /** Children own a Gorhom scrollable; do not register a static view. */
  hasScrollableContent?: boolean;
  /** Fires after the sheet's dismissal animation completes. */
  onDismiss?: () => void;
  ref?: Ref<BottomSheetHandle>;
  scrollable?: boolean;
};

/**
 * App-styled modal bottom sheet wrapping `@gorhom/bottom-sheet`'s
 * `BottomSheetModal`. Handles the backdrop, rounded handle, light/dark surface,
 * safe-area bottom padding, and a scoped context bridge (so React context — e.g.
 * the actor database — crosses the sheet's portal boundary). Content is
 * dynamically sized to fit its children.
 *
 * Requires a `BottomSheetModalProvider` ancestor (already mounted by
 * `@-/dev.improve.haniverse.routes._context`). Drive it via the imperative
 * `{ open, close }` handle on `ref`.
 */
export function BottomSheet({ onDismiss, ref, ...props }: BottomSheetProps) {
  const [mounted, setMounted] = useState(false);
  const sheetRef = useRef<BottomSheetHandle | null>(null);
  const pendingOpen = useRef(false);
  const retainBody = useRouteBodyRetainer();
  const releaseBodyRef = useRef<(() => void) | null>(null);
  const releaseBody = useCallback(() => {
    releaseBodyRef.current?.();
    releaseBodyRef.current = null;
  }, []);
  useEffect(() => releaseBody, [releaseBody]);
  const present = useCallback(
    (sheet: BottomSheetHandle) => {
      pendingOpen.current = false;
      try {
        sheet.open();
      } catch (error) {
        setMounted(false);
        releaseBody();
        throw error;
      }
    },
    [releaseBody],
  );
  const bindSheet = useCallback(
    (sheet: BottomSheetHandle | null) => {
      sheetRef.current = sheet;
      if (sheet !== null && pendingOpen.current) {
        present(sheet);
      }
    },
    [present],
  );
  useImperativeHandle(
    ref,
    () => ({
      close: () => {
        pendingOpen.current = false;
        if (sheetRef.current !== null) {
          sheetRef.current.close();
        } else {
          setMounted(false);
          releaseBody();
        }
      },
      open: () => {
        // Acquire before rendering the portal: a tab change must not retire its source body
        // while the first presentation is still waiting for React to commit.
        releaseBodyRef.current ??= retainBody();
        pendingOpen.current = true;
        if (sheetRef.current !== null) {
          present(sheetRef.current);
        } else {
          setMounted(true);
        }
      },
    }),
    [present, releaseBody, retainBody],
  );
  const handleDismiss = useCallback(() => {
    pendingOpen.current = false;
    setMounted(false);
    try {
      onDismiss?.();
    } finally {
      releaseBody();
    }
  }, [onDismiss, releaseBody]);

  // Closed sheets must not capture/subscribe to every bridged context, keyboard and theme.
  // Prepared Learn cards otherwise pay this cost on every focus and review update even
  // though Gorhom itself renders no modal content until present(). Keep it through dismissal.
  return mounted ? (
    <MountedBottomSheet {...props} onDismiss={handleDismiss} ref={bindSheet} />
  ) : null;
}

function MountedBottomSheet({
  animatedIndex,
  children,
  enableContentPanningGesture = true,
  footer,
  fullHeight = false,
  hasScrollableContent = false,
  onDismiss,
  ref,
  scrollable = false,
}: BottomSheetProps) {
  const contextValues = usePortalContextValues();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  // The HOST window's insets, not the caller's: the sheet renders at the provider's portal host,
  // and a native modal screen reports a top inset of 0, which let full-height sheets cover the
  // status bar.
  const insets = useBottomSheetHostSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const colorScheme = useTheme();
  const keyboardHeight = useKeyboardState((state) => (state.isVisible ? state.height : 0));
  // Keep the sheet's container full-height and reserve the keyboard's
  // space as CONTENT padding instead of lifting the container (`bottomInset`).
  // Lifting is racy: a modal sheet's `containerHeight` is measured once by the
  // shared modal provider and never reflects a runtime `bottomInset`, so the
  // dynamic detent is computed as if the sheet could reach the screen bottom
  // while the real container ends at the keyboard — the final position then
  // depended on whether gorhom's keyboard listener happened to fire during the
  // present animation (it never fires when the keyboard was already up, e.g. a
  // sheet presented over the review answer field's keyboard), clipping up to a
  // full keyboard-height of content. Padding makes the dynamic detent
  // keyboard-aware by construction — no keyboard-event timing involved — and
  // the sheet surface extends down behind the keyboard, so the sheet (not the
  // dimmed backdrop) shows through the iOS 26 keyboard's transparent
  // rounded-corner notches.
  // Android needs the same: `KeyboardProvider` (react-native-keyboard-controller) keeps the
  // window edge-to-edge and never resizes it for the IME, so the keyboard overlays the sheet just
  // like on iOS, and a runtime `bottomInset` hits the same stale `containerHeight` bug — the
  // container was clipped at the keyboard top while the sheet stayed positioned for the full
  // screen, hiding the auth sheet's password field behind the keyboard (every Android Maestro
  // sign-in since the email form moved into this sheet). Web reports no keyboard height.
  const keyboardContentInset = keyboardHeight;

  const backgroundStyle = useMemo(
    () => ({
      backgroundColor: colorScheme === "dark" ? DARK_BACKGROUND : LIGHT_BACKGROUND,
      borderRadius: 0,
      borderTopLeftRadius: SHEET_RADIUS,
      borderTopRightRadius: SHEET_RADIUS,
    }),
    [colorScheme],
  );

  // When the keyboard is up, its reserved space replaces the home-indicator
  // inset (the safe area sits behind the keyboard).
  const contentStyle = useMemo(
    () => ({
      // A nested navigator needs a bounded viewport. Flex alone uses the modal
      // container's full screen height and leaves the last content below the sheet.
      ...(fullHeight && !scrollable
        ? {
            height: Math.max(
              0,
              windowHeight - insets.top - SHEET_TOP_GAP - HANDLE_CONTAINER_HEIGHT,
            ),
          }
        : {}),
      paddingBottom:
        (keyboardContentInset > 0 ? keyboardContentInset : insets.bottom) +
        16 +
        (footer === undefined ? 0 : FOOTER_CONTENT_INSET),
    }),
    [footer, fullHeight, insets.bottom, insets.top, keyboardContentInset, scrollable, windowHeight],
  );

  useImperativeHandle(
    ref,
    () => ({
      close: () => {
        bottomSheetRef.current?.dismiss();
      },
      open: () => {
        bottomSheetRef.current?.present();
      },
    }),
    [],
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        opacity={0.5}
        pressBehavior="close"
      />
    ),
    [],
  );

  const renderFooter = useCallback(
    (props: BottomSheetFooterProps) => (
      <BottomSheetFooter {...props} bottomInset={insets.bottom + 8}>
        <BottomSheetContentErrorBoundary onClose={() => bottomSheetRef.current?.dismiss()}>
          <PortalContextBridgeContent values={contextValues}>
            <View className="px-4">{footer}</View>
          </PortalContextBridgeContent>
        </BottomSheetContentErrorBoundary>
      </BottomSheetFooter>
    ),
    [contextValues, footer, insets.bottom],
  );

  // BottomSheetView registers itself as the sheet's active STATIC content in a parent
  // effect. Wrapping a registered FlatList with it overwrites the child's scrollable type,
  // making upward content pans over-drag the sheet and lock/reset the list to offset zero.
  // A caller-owned scrollable needs only the existing layout box, not another registration.
  const ContentContainer = hasScrollableContent ? View : BottomSheetView;

  return (
    <BottomSheetModal
      accessible={false}
      android_keyboardInputMode="adjustResize"
      animatedIndex={animatedIndex}
      backdropComponent={renderBackdrop}
      backgroundStyle={backgroundStyle}
      enableContentPanningGesture={enableContentPanningGesture}
      enableDynamicSizing={!fullHeight}
      footerComponent={footer === undefined ? undefined : renderFooter}
      handleIndicatorStyle={styles.handleIndicator}
      // NOT "interactive": that mode subtracts the keyboard height from the
      // sheet position a second time (our content padding already reserves it)
      // and freezes the sheet in a "temporary position" that ignores later
      // detent updates. "extend" just re-targets the highest detent — which is
      // already keyboard-aware via the content padding.
      keyboardBehavior="extend"
      keyboardBlurBehavior="restore"
      onDismiss={onDismiss}
      ref={bottomSheetRef}
      snapPoints={fullHeight ? ["100%"] : undefined}
      topInset={insets.top + SHEET_TOP_GAP}
    >
      {scrollable ? (
        // "handled": with the keyboard up, a tap on a control (a mode toggle, a submit button)
        // acts on it, instead of only dismissing the keyboard and swallowing the press.
        <BottomSheetScrollView
          contentContainerStyle={contentStyle}
          keyboardShouldPersistTaps="handled"
        >
          <BottomSheetContentErrorBoundary onClose={() => bottomSheetRef.current?.dismiss()}>
            <PortalContextBridgeContent values={contextValues}>
              <View className="px-4 pt-1">{children}</View>
            </PortalContextBridgeContent>
          </BottomSheetContentErrorBoundary>
        </BottomSheetScrollView>
      ) : (
        <ContentContainer
          accessible={false}
          // Preserve BottomSheetView's absolute layout geometry without its registration effect.
          className={hasScrollableContent ? "absolute inset-x-0 top-0" : undefined}
          style={contentStyle}
        >
          <BottomSheetContentErrorBoundary onClose={() => bottomSheetRef.current?.dismiss()}>
            <PortalContextBridgeContent values={contextValues}>
              <View className={fullHeight ? "flex-1 px-4 pt-1" : "px-4 pt-1"}>{children}</View>
            </PortalContextBridgeContent>
          </BottomSheetContentErrorBoundary>
        </ContentContainer>
      )}
    </BottomSheetModal>
  );
}
